import { Injectable } from '@angular/core';
import { LoginResponse } from '../../models/login-response.model';
import { LocalStorageService } from '../local-storage/local-storage.service';

/**
 * Encapsulates the admin-impersonation session swap.
 *
 * The login flow (AuthenticationService.loginUser) persists the session under
 * these localStorage keys via LocalStorageService.set (JSON.stringified):
 *   - appToken     : JWT (also read by the api-key + auth-error interceptors)
 *   - appUserName  : username
 *   - appUserId    : user id
 *   - appUseGroup  : userGroupId   (note: backend/login uses this exact key spelling)
 *   - appModules   : ModulePermission[] (drives the sidebar + permissions)
 *
 * Impersonation overwrites those SAME keys with the impersonate response so the
 * impersonated session's sidebar/permissions reflect the target user, after first
 * backing up the admin's values into parallel `impersonator_*` keys so they can be
 * restored on stop. A small set of flags is persisted so a page reload keeps the banner.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  // Session keys written by the login flow (must match AuthenticationService).
  private readonly SESSION_KEYS = ['appToken', 'appUserName', 'appUserId', 'appUseGroup', 'appModules'];

  // Backup keys holding the admin's session while impersonating.
  private readonly BACKUP_PREFIX = 'impersonator_';
  private readonly FLAG_KEY = 'impersonating';
  private readonly NAME_KEY = 'impersonatedName';
  private readonly ADMIN_NAME_KEY = 'impersonatorName';

  // Landing route used by the login flow ( '/' -> '/app' -> 'tasks' ).
  private readonly HOME_ROUTE = '/app/tasks';

  constructor(private readonly localStorage: LocalStorageService) {}

  isImpersonating(): boolean {
    return this.localStorage.getNull(this.FLAG_KEY) === true;
  }

  getImpersonatedName(): string {
    return this.localStorage.getNull(this.NAME_KEY) ?? '';
  }

  getImpersonatorName(): string {
    return this.localStorage.getNull(this.ADMIN_NAME_KEY) ?? '';
  }

  /**
   * Back up the current admin session, write the impersonated session into the
   * login keys, flag the banner, then reload to the home route to rebuild all state.
   */
  start(response: LoginResponse): void {
    if (this.isImpersonating()) {
      // Already impersonating: keep the original admin backup intact, just swap the session.
    } else {
      // Capture the admin's display name for the "Stop" affordance.
      const adminName = localStorage.getItem('appUserName');
      this.SESSION_KEYS.forEach((key) => {
        const raw = localStorage.getItem(key);
        if (raw !== null) {
          localStorage.setItem(this.BACKUP_PREFIX + key, raw);
        } else {
          localStorage.removeItem(this.BACKUP_PREFIX + key);
        }
      });
      if (adminName !== null) {
        localStorage.setItem(this.ADMIN_NAME_KEY, adminName);
      }
    }

    // Write the impersonated session into the SAME keys login uses.
    this.localStorage.set('appToken', response.token);
    this.localStorage.set('appUserName', response.username);
    this.localStorage.set('appUserId', response.id);
    this.localStorage.set('appUseGroup', response.userGroupId);
    this.localStorage.set('appModules', JSON.stringify(response.modules));

    this.localStorage.set(this.FLAG_KEY, true);
    this.localStorage.set(this.NAME_KEY, response.username);

    this.reloadHome();
  }

  /**
   * Restore the backed-up admin session, clear impersonation state, then reload
   * to the home route so the admin sidebar/permissions are rebuilt.
   */
  stop(): void {
    this.SESSION_KEYS.forEach((key) => {
      const backupKey = this.BACKUP_PREFIX + key;
      const raw = localStorage.getItem(backupKey);
      if (raw !== null) {
        localStorage.setItem(key, raw);
      } else {
        localStorage.removeItem(key);
      }
      localStorage.removeItem(backupKey);
    });

    this.localStorage.remove(this.FLAG_KEY);
    this.localStorage.remove(this.NAME_KEY);
    this.localStorage.remove(this.ADMIN_NAME_KEY);

    this.reloadHome();
  }

  private reloadHome(): void {
    // A full location reload to the home route is the simplest way to rebuild the
    // shell, sidebar and permissions from the freshly written `appModules`.
    window.location.assign(this.HOME_ROUTE);
  }
}
